import {
  NodeApi,
  NodeRendererProps,
  Tree,
  TreeApi,
  SimpleTree,
} from "react-arborist";
import { atom, useAtom, useSetAtom } from "jotai";
import { useTranslationStatus } from "@/features/translation/queries/translation-query.ts";
import { translationStatusAtom } from "@/features/translation/atoms/translation-status-atom.ts";
import { TranslationDot } from "@/features/translation/components/translation-dot.tsx";
import { treeApiAtom } from "@/features/page/tree/atoms/tree-api-atom.ts";
import { setPendingCreateExtra } from "@/features/page/tree/atoms/pending-create-atom.ts";
import {
  closeExternalLinkModal,
  openExternalLinkModal,
  useExternalLinkModal,
} from "@/features/page/tree/atoms/external-link-modal.ts";
import { editLinkAtom, EditLinkState } from "@/features/page/tree/atoms/edit-link-atom";
import {
  fetchAllAncestorChildren,
  useGetRootSidebarPagesQuery,
  usePageQuery,
  useUpdatePageMutation,
} from "@/features/page/queries/page-query.ts";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import classes from "@/features/page/tree/styles/tree.module.css";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Menu,
  Modal,
  rem,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import {
  IconArrowRight,
  IconCategory,
  IconChevronDown,
  IconChevronRight,
  IconCopy,
  IconDotsVertical,
  IconExternalLink,
  IconEye,
  IconEyeOff,
  IconFileDescription,
  IconFileExport,
  IconFold,
  IconFoldDown,
  IconFolder,
  IconLayoutNavbar,
  IconLayoutNavbarFilled,
  IconLink,
  IconPlus,
  IconPointFilled,
  IconStar,
  IconStarFilled,
  IconTrash,
} from "@tabler/icons-react";
import {
  appendNodeChildrenAtom,
  treeDataAtom,
} from "@/features/page/tree/atoms/tree-data-atom.ts";
import clsx from "clsx";
import EmojiPicker from "@/components/ui/emoji-picker.tsx";
import { openIconPicker } from "@/features/space/components/sidebar-links-editor.tsx";
import { getSidebarIcon } from "@/features/space/lib/sidebar-icons.tsx";
import { useTreeMutation } from "@/features/page/tree/hooks/use-tree-mutation.ts";
import {
  appendNodeChildren,
  buildTree,
  buildTreeWithChildren,
  deleteTreeNode,
  mergeRootTrees,
  updateTreeNodeIcon,
  updateTreeNodeName,
} from "@/features/page/tree/utils/utils.ts";
import { SpaceTreeNode } from "@/features/page/tree/types.ts";
import {
  getAllSidebarPages,
  getPageBreadcrumbs,
  getPageById,
  getSidebarPages,
  movePage as movePageService,
} from "@/features/page/services/page-service.ts";
import { useTabsQuery } from "@/features/tab/queries/tab-query.ts";
import { activeTabBySpaceAtom } from "@/features/tab/atoms/active-tab-atom.ts";
import { generateJitteredKeyBetween as generateJitteredKeyBetweenClient } from "fractional-indexing-jittered";
import { IPage, SidebarPagesParams } from "@/features/page/types/page.types.ts";
import { queryClient } from "@/main.tsx";
import { OpenMap } from "react-arborist/dist/main/state/open-slice";
import { useDisclosure, useElementSize, useMergedRef } from "@mantine/hooks";
import { useClipboard } from "@/hooks/use-clipboard";
import { dfs } from "react-arborist/dist/module/utils";
import { useQueryEmit } from "@/features/websocket/use-query-emit.ts";
import { buildPageUrl } from "@/features/page/page.utils.ts";
import { notifications } from "@mantine/notifications";
import { getAppUrl } from "@/lib/config.ts";
import { extractPageSlugId } from "@/lib";
import { useDeletePageModal } from "@/features/page/hooks/use-delete-page-modal.tsx";
import { useTranslation } from "react-i18next";
import ExportModal from "@/components/common/export-modal";
import MovePageModal from "../../components/move-page-modal.tsx";
import { mobileSidebarAtom } from "@/components/layouts/global/hooks/atoms/sidebar-atom.ts";
import { useToggleSidebar } from "@/components/layouts/global/hooks/hooks/use-toggle-sidebar.ts";
import CopyPageModal from "../../components/copy-page-modal.tsx";
import { duplicatePage } from "../../services/page-service.ts";
import { useFavoriteIds, useAddFavoriteMutation, useRemoveFavoriteMutation } from "@/features/favorite/queries/favorite-query";

interface SpaceTreeProps {
  spaceId: string;
  readOnly: boolean;
  showHidden?: boolean;
  // Scope of the top-level fetch:
  //   - undefined → entire space (single-tree mode),
  //   - "loose"   → only pages with tab_id IS NULL,
  //   - <uuid>    → only pages inside that tab.
  tabId?: string | "loose";
}

const openTreeNodesAtom = atom<OpenMap>({});

export default function SpaceTree({ spaceId, readOnly, showHidden = true, tabId }: SpaceTreeProps) {
  const { t } = useTranslation();
  const { pageSlug } = useParams();
  // Top-level subscription for translation status: populate the global
  // atom that every Node row reads to render its colored dot. Polls
  // while there are pending translations, stops when all ready/failed.
  const trStatusQuery = useTranslationStatus(spaceId);
  const setTrStatus = useSetAtom(translationStatusAtom);
  useEffect(() => {
    setTrStatus(trStatusQuery.data ?? null);
  }, [trStatusQuery.data, setTrStatus]);
  const { data, setData, controllers } =
    useTreeMutation<TreeApi<SpaceTreeNode>>(spaceId);
  const {
    data: pagesData,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useGetRootSidebarPagesQuery({
    spaceId,
    // @ts-ignore: tabId is a backend-supported filter
    tabId,
  });
  const [, setTreeApi] = useAtom<TreeApi<SpaceTreeNode>>(treeApiAtom);
  const treeApiRef = useRef<TreeApi<SpaceTreeNode>>(null);
  const [openTreeNodes, setOpenTreeNodes] = useAtom<OpenMap>(openTreeNodesAtom);
  const rootElement = useRef<HTMLDivElement>(null);
  const [isRootReady, setIsRootReady] = useState(false);
  const { ref: sizeRef, width, height } = useElementSize();
  const mergedRef = useMergedRef((element) => {
    rootElement.current = element;
    if (element && !isRootReady) {
      setIsRootReady(true);
    }
  }, sizeRef);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const spaceIdRef = useRef(spaceId);
  spaceIdRef.current = spaceId;
  const { data: currentPage } = usePageQuery({
    pageId: extractPageSlugId(pageSlug),
  });

  useEffect(() => {
    // Switching tabs (or spaces) must drop the merged tree atom — otherwise
    // pages from the previous tab linger and visually appear under the new
    // one until a write triggers a full re-render.
    setIsDataLoaded(false);
    setData([]);
    setOpenTreeNodes({});
  }, [spaceId, tabId]);

  // Auto-switch active tab when the current page lives in a different tab
  // (e.g. user opened a deep link, used search, or clicked a breadcrumb).
  // Without this, the sidebar would render empty until the user manually
  // clicks the right pill.
  const [activeTabBySpace, setActiveTabBySpace] = useAtom(activeTabBySpaceAtom);
  useEffect(() => {
    const pageTabId = (currentPage as any)?.tabId as string | null | undefined;
    if (!currentPage?.id || pageTabId == null) return;
    if (currentPage.spaceId !== spaceId) return;
    const current = activeTabBySpace[spaceId];
    if (current === pageTabId) return;
    setActiveTabBySpace((prev) => ({ ...prev, [spaceId]: pageTabId }));
  }, [currentPage?.id, currentPage?.spaceId, spaceId]);

  useEffect(() => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, isFetching, spaceId]);

  useEffect(() => {
    if (pagesData?.pages && !hasNextPage) {
      const allItems = pagesData.pages.flatMap((page) => page.items);
      const treeData = buildTree(allItems);

      setData((prev) => {
        // fresh space; full reset
        if (prev.length === 0 || prev[0]?.spaceId !== spaceId) {
          setIsDataLoaded(true);
          setOpenTreeNodes({});
          return treeData;
        }

        // same space; append only missing roots
        setIsDataLoaded(true);
        return mergeRootTrees(prev, treeData);
      });
    }
  }, [pagesData, hasNextPage, spaceId]);

  useEffect(() => {
    const effectSpaceId = spaceId;

    const fetchData = async () => {
      if (isDataLoaded && currentPage) {
        // Don't graft the current page's ancestors into a tree that doesn't
        // belong to it. Without this gate, opening a page that lives in
        // tab A while tab B is active leaks A's branch into B's sidebar.
        // tabId === undefined means the space has no tabs (legacy mode).
        if (
          tabId !== undefined &&
          (currentPage as any).tabId !== tabId
        ) {
          return;
        }

        // check if pageId node is present in the tree
        const node = dfs(treeApiRef.current?.root, currentPage.id);
        if (node) {
          // if node is found, no need to traverse its ancestors
          return;
        }

        // if not found, fetch and build its ancestors and their children
        if (!currentPage.id) return;
        const ancestors = await getPageBreadcrumbs(currentPage.id);

        if (spaceIdRef.current !== effectSpaceId) return;

        if (ancestors && ancestors?.length > 1) {
          let flatTreeItems = [...buildTree(ancestors)];

          const fetchAndUpdateChildren = async (ancestor: IPage) => {
            // we don't want to fetch the children of the opened page
            if (ancestor.id === currentPage.id) {
              return;
            }
            const children = await fetchAllAncestorChildren({
              pageId: ancestor.id,
              spaceId: ancestor.spaceId,
            });

            flatTreeItems = [
              ...flatTreeItems,
              ...children.filter(
                (child) => !flatTreeItems.some((item) => item.id === child.id),
              ),
            ];
          };

          const fetchPromises = ancestors.map((ancestor) =>
            fetchAndUpdateChildren(ancestor),
          );

          // Wait for all fetch operations to complete
          Promise.all(fetchPromises).then(() => {
            if (spaceIdRef.current !== effectSpaceId) return;

            // build tree with children
            const ancestorsTree = buildTreeWithChildren(flatTreeItems);
            // child of root page we're attaching the built ancestors to
            const rootChild = ancestorsTree[0];

            // attach built ancestors to tree using functional updater
            // to avoid stale closure overwriting the current tree data
            setData((currentData) =>
              appendNodeChildren(currentData, rootChild.id, rootChild.children),
            );

            setTimeout(() => {
              // focus on node and open all parents
              treeApiRef.current?.select(currentPage.id);
            }, 100);
          });
        }
      }
    };

    fetchData();
  }, [isDataLoaded, currentPage?.id, tabId]);

  useEffect(() => {
    if (currentPage?.id) {
      setTimeout(() => {
        // focus on node and open all parents
        treeApiRef.current?.select(currentPage.id, { align: "auto" });
      }, 200);
    } else {
      treeApiRef.current?.deselectAll();
    }
  }, [currentPage?.id]);

  // Clean up tree API on unmount
  useEffect(() => {
    return () => {
      // @ts-ignore
      setTreeApi(null);
    };
  }, [setTreeApi]);

  const filterHidden = (nodes: SpaceTreeNode[]): SpaceTreeNode[] =>
    nodes
      .filter((n) => showHidden || !n?.hidden)
      .map((n) => ({ ...n, children: n.children ? filterHidden(n.children) : [] }));

  const filteredData = filterHidden(
    data.filter((node) => node?.spaceId === spaceId),
  );

  return (
    <div ref={mergedRef} className={classes.treeContainer}>
      {isDataLoaded && filteredData.length === 0 && (
        <Text size="xs" c="dimmed" py="xs" px="sm">
          {t("No pages yet")}
        </Text>
      )}
      {isRootReady && rootElement.current && (
        <Tree
          data={filteredData}
          disableDrag={
            readOnly
              ? true
              : (data) => {
                  return data.canEdit === false;
                }
          }
          disableDrop={
            readOnly
              ? true
              : ({ parentNode }) => parentNode?.data?.canEdit === false
          }
          disableEdit={readOnly ? true : (data) => data.canEdit === false}
          {...controllers}
          width={width}
          height={rootElement.current.clientHeight}
          ref={(ref) => {
            treeApiRef.current = ref;
            if (ref) {
              //@ts-ignore
              setTreeApi(ref);
            }
          }}
          openByDefault={false}
          disableMultiSelection={true}
          className={classes.tree}
          rowClassName={classes.row}
          rowHeight={30}
          overscanCount={10}
          dndRootElement={rootElement.current}
          onToggle={() => {
            setOpenTreeNodes(treeApiRef.current?.openState);
          }}
          initialOpenState={openTreeNodes}
        >
          {Node}
        </Tree>
      )}
      {/* Mounted at the SpaceTree root, sibling to <Tree> — not inside any
          row — so react-arborist's keyboard/focus handling can't reach the
          modal's inputs. */}
      <ExternalLinkCreateModal />
    </div>
  );
}

function Node({ node, style, dragHandle, tree }: NodeRendererProps<any>) {
  const { t } = useTranslation();
  const updatePageMutation = useUpdatePageMutation();
  const [treeData, setTreeData] = useAtom(treeDataAtom);
  const [, appendChildren] = useAtom(appendNodeChildrenAtom);
  const emit = useQueryEmit();
  const { spaceSlug } = useParams();
  const timerRef = useRef(null);
  const [mobileSidebarOpened] = useAtom(mobileSidebarAtom);
  const toggleMobileSidebar = useToggleSidebar(mobileSidebarAtom);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(node.data.name || "");

  const canEditNode =
    tree.props.disableEdit !== true && node.data.canEdit !== false;

  const isGroup = !!node.data.isGroup;
  const isLink = !!node.data.externalUrl;
  const isHidden = !!node.data.hidden;
  const hiddenStyle = isHidden ? { opacity: 0.55 } : undefined;
  const HiddenMark = isHidden ? (
    <span style={{ display: "inline-flex", marginRight: 2 }} title={t("Hidden")}>
      <IconEyeOff size={14} stroke={2} />
    </span>
  ) : null;
  const CollapsedMark =
    isGroup && node.data.groupCollapsed ? (
      <span
        style={{ display: "inline-flex", marginLeft: 4, opacity: 0.55 }}
        title={t("Collapsed by default on the public site")}
      >
        <IconFold size={14} stroke={2} />
      </span>
    ) : null;

  useEffect(() => {
    if (!isRenaming) {
      setRenameValue(node.data.name || "");
    }
  }, [node.data.name, isRenaming]);

  const commitRename = () => {
    const trimmed = (renameValue ?? "").trim();
    setIsRenaming(false);
    if (trimmed === (node.data.name || "").trim()) {
      return;
    }
    // Local optimistic update
    const updated = updateTreeNodeName(treeData, node.id, trimmed);
    setTreeData(updated);
    updatePageMutation
      .mutateAsync({ pageId: node.id, title: trimmed })
      .then(() => {
        setTimeout(() => {
          emit({
            operation: "updateOne",
            spaceId: node.data.spaceId,
            entity: ["pages"],
            id: node.id,
            payload: { title: trimmed },
          });
        }, 50);
      })
      .catch(() => {
        // revert on failure
        const reverted = updateTreeNodeName(
          treeData,
          node.id,
          node.data.name || "",
        );
        setTreeData(reverted);
      });
  };

  const handleDoubleClick = (e: any) => {
    if (!canEditNode) return;
    e.preventDefault();
    e.stopPropagation();
    setRenameValue(node.data.name || "");
    setIsRenaming(true);
  };

  const prefetchPage = () => {
    timerRef.current = setTimeout(async () => {
      const page = await queryClient.fetchQuery({
        queryKey: ["pages", node.data.id],
        queryFn: () => getPageById({ pageId: node.data.id }),
        staleTime: 5 * 60 * 1000,
      });
      if (page?.slugId) {
        queryClient.setQueryData(["pages", page.slugId], page);
      }
    }, 150);
  };

  const cancelPagePrefetch = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  async function handleLoadChildren(node: NodeApi<SpaceTreeNode>) {
    if (!node.data.hasChildren) return;
    // in conflict with use-query-subscription.ts => case "addTreeNode","moveTreeNode" etc with websocket
    // if (node.data.children && node.data.children.length > 0) {
    //   return;
    // }

    try {
      const params: SidebarPagesParams = {
        pageId: node.data.id,
        spaceId: node.data.spaceId,
      };

      const childrenTree = await fetchAllAncestorChildren(params);

      appendChildren({
        parentId: node.data.id,
        children: childrenTree,
      });
    } catch (error) {
      console.error("Failed to fetch children:", error);
    }
  }

  const handleUpdateNodeIcon = (nodeId: string, newIcon: string) => {
    const updatedTree = updateTreeNodeIcon(treeData, nodeId, newIcon);
    setTreeData(updatedTree);
  };

  const handleEmojiIconClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleEmojiSelect = (emoji: { native: string }) => {
    handleUpdateNodeIcon(node.id, emoji.native);
    updatePageMutation
      .mutateAsync({ pageId: node.id, icon: emoji.native })
      .then((data) => {
        setTimeout(() => {
          emit({
            operation: "updateOne",
            spaceId: node.data.spaceId,
            entity: ["pages"],
            id: node.id,
            payload: { icon: emoji.native, parentPageId: data.parentPageId },
          });
        }, 50);
      });
  };

  const handleRemoveEmoji = () => {
    handleUpdateNodeIcon(node.id, null);
    updatePageMutation.mutateAsync({ pageId: node.id, icon: null });

    setTimeout(() => {
      emit({
        operation: "updateOne",
        spaceId: node.data.spaceId,
        entity: ["pages"],
        id: node.id,
        payload: { icon: null },
      });
    }, 50);
  };

  // Group headers use the curated icon library (same set as sidebar
  // links), stored in pages.icon as `licon:<key>` so it can't be
  // confused with page emoji.
  const groupIconKey = /^licon:([\w-]+)$/.exec(node.data.icon ?? "")?.[1];
  const GroupIconCmp = groupIconKey ? getSidebarIcon(groupIconKey) : null;

  const handleGroupIconClick = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canEditNode) return;
    const picked = await openIconPicker(groupIconKey ?? "", {
      allowClear: !!groupIconKey,
    });
    if (picked === null) return; // cancelled
    const iconValue = picked === "" ? null : `licon:${picked}`;
    handleUpdateNodeIcon(node.id, iconValue);
    updatePageMutation
      .mutateAsync({ pageId: node.id, icon: iconValue })
      .then(() => {
        setTimeout(() => {
          emit({
            operation: "updateOne",
            spaceId: node.data.spaceId,
            entity: ["pages"],
            id: node.id,
            payload: { icon: iconValue },
          });
        }, 50);
      });
  };

  if (
    node.willReceiveDrop &&
    node.isClosed &&
    (node.children.length > 0 || node.data.hasChildren)
  ) {
    handleLoadChildren(node);
    setTimeout(() => {
      if (node.state.willReceiveDrop) {
        node.open();
      }
    }, 650);
  }

  const pageUrl = buildPageUrl(spaceSlug, node.data.slugId, node.data.name);

  // Group node: uppercase section header, not clickable, no icon
  if (isGroup) {
    return (
      <Box
        style={{ ...style, ...hiddenStyle }}
        className={clsx(classes.node, classes.groupNode, node.state)}
        // @ts-ignore
        ref={dragHandle}
        onDoubleClick={handleDoubleClick}
      >
        <PageArrow node={node} onExpandTree={() => handleLoadChildren(node)} />
        {HiddenMark}

        {(GroupIconCmp || canEditNode) && (
          <span
            onClick={handleGroupIconClick}
            title={canEditNode ? t("Pick icon") : undefined}
            style={{
              display: "inline-flex",
              alignItems: "center",
              marginRight: 4,
              cursor: canEditNode ? "pointer" : undefined,
              opacity: GroupIconCmp ? 1 : 0.4,
            }}
          >
            {GroupIconCmp ? (
              <GroupIconCmp size={14} />
            ) : (
              <IconCategory size={14} stroke={2} />
            )}
          </span>
        )}

        {isRenaming ? (
          <input
            autoFocus
            className={classes.renameInput}
            value={renameValue}
            onChange={(e) => setRenameValue(e.currentTarget.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                (e.currentTarget as HTMLInputElement).blur();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setIsRenaming(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className={classes.groupText}>
            {node.data.name || t("New group")}
          </span>
        )}
        {CollapsedMark}

        <div className={classes.actions}>
          <TranslationDot pageId={node.data.id} />
          <NodeMenu node={node} treeApi={tree} spaceId={node.data.spaceId} />

          {canEditNode && (
            <CreateNode
              node={node}
              treeApi={tree}
              onExpandTree={() => handleLoadChildren(node)}
            />
          )}
        </div>
      </Box>
    );
  }

  // External link node: anchor opening new tab, no router navigation
  if (isLink) {
    return (
      <Box
        style={{ ...style, ...hiddenStyle }}
        className={clsx(classes.node, node.state)}
        component="a"
        href={node.data.externalUrl || "#"}
        target="_blank"
        rel="noreferrer noopener"
        // @ts-ignore
        ref={dragHandle}
        onClick={(e: any) => {
          if (isRenaming) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          if (mobileSidebarOpened) {
            toggleMobileSidebar();
          }
        }}
        onDoubleClick={(e: any) => {
          if (canEditNode) {
            e.preventDefault();
          }
          handleDoubleClick(e);
        }}
      >
        <ActionIcon size={20} variant="subtle" c="gray" component="span">
          <IconExternalLink stroke={2} size={16} />
        </ActionIcon>

        {HiddenMark}

        <div style={{ marginRight: "4px", marginLeft: "4px" }}>
          <IconLink size={18} />
        </div>

        {isRenaming ? (
          <input
            autoFocus
            className={classes.renameInput}
            value={renameValue}
            onChange={(e) => setRenameValue(e.currentTarget.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                (e.currentTarget as HTMLInputElement).blur();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setIsRenaming(false);
              }
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
        ) : (
          <span className={classes.text}>
            {node.data.name || t("External link")}
          </span>
        )}

        <div className={classes.actions}>
          <TranslationDot pageId={node.data.id} />
          <NodeMenu node={node} treeApi={tree} spaceId={node.data.spaceId} />
        </div>
      </Box>
    );
  }

  return (
    <>
      <Box
        style={{ ...style, ...hiddenStyle }}
        className={clsx(classes.node, node.state)}
        component={Link}
        to={pageUrl}
        // @ts-ignore
        ref={dragHandle}
        onClick={(e: any) => {
          if (isRenaming) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          if (mobileSidebarOpened) {
            toggleMobileSidebar();
          }
        }}
        onDoubleClick={(e: any) => {
          if (canEditNode) {
            e.preventDefault();
          }
          handleDoubleClick(e);
        }}
        onMouseEnter={prefetchPage}
        onMouseLeave={cancelPagePrefetch}
      >
        <PageArrow node={node} onExpandTree={() => handleLoadChildren(node)} />

        {HiddenMark}

        <div onClick={handleEmojiIconClick} style={{ marginRight: "4px" }}>
          <EmojiPicker
            onEmojiSelect={handleEmojiSelect}
            icon={
              node.data.icon ? (
                node.data.icon
              ) : (
                <IconFileDescription size="18" />
              )
            }
            readOnly={
              tree.props.disableEdit === true || node.data.canEdit === false
            }
            removeEmojiAction={handleRemoveEmoji}
          />
        </div>

        {isRenaming ? (
          <input
            autoFocus
            className={classes.renameInput}
            value={renameValue}
            onChange={(e) => setRenameValue(e.currentTarget.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                (e.currentTarget as HTMLInputElement).blur();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setIsRenaming(false);
              }
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
        ) : (
          <span className={classes.text}>
            {node.data.name || t("untitled")}
          </span>
        )}

        <div className={classes.actions}>
          <TranslationDot pageId={node.data.id} />
          <NodeMenu node={node} treeApi={tree} spaceId={node.data.spaceId} />

          {canEditNode && (
            <CreateNode
              node={node}
              treeApi={tree}
              onExpandTree={() => handleLoadChildren(node)}
            />
          )}
        </div>
      </Box>
    </>
  );
}

interface CreateNodeProps {
  node: NodeApi<SpaceTreeNode>;
  treeApi: TreeApi<SpaceTreeNode>;
  onExpandTree?: () => void;
}

function CreateNode({ node, treeApi, onExpandTree }: CreateNodeProps) {
  const { t } = useTranslation();

  const createChild = (extra?: {
    kind?: "page" | "group" | "link";
    externalUrl?: string;
    title?: string;
  }) => {
    const doCreate = () => {
      // react-arborist v3 drops the `data` arg of tree.create; we route the
      // extras through a module-level side channel that onCreate consumes.
      // See pending-create-atom.ts.
      setPendingCreateExtra(extra ?? null);
      treeApi?.create({
        type: "internal",
        parentId: node.id,
      });
    };

    if (node.data.hasChildren && node.children.length === 0) {
      node.toggle();
      onExpandTree();
      setTimeout(doCreate, 500);
    } else {
      doCreate();
    }
  };

  const openExternalLink = () => {
    // Render the modal at the SpaceTree top level, not here. Mounting it
    // inside a tree row let react-arborist's row focus + keyboard
    // handlers fight the text inputs on every keystroke — the inputs were
    // effectively uneditable. The hoisted modal is outside <Tree>'s
    // subtree entirely.
    openExternalLinkModal({
      parentNodeId: String(node.id),
      onSubmit: ({ title, url }) => {
        createChild({
          kind: "link",
          externalUrl: url,
          title: title || url,
        });
      },
    });
  };

  return (
    <Menu shadow="md" width={200} position="bottom-end" withinPortal>
      <Menu.Target>
        <ActionIcon
          variant="transparent"
          c="gray"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <IconPlus style={{ width: rem(20), height: rem(20) }} stroke={2} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconFileDescription size={16} />}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            createChild({ kind: "page" });
          }}
        >
          {t("New page")}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconFolder size={16} />}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            createChild({ kind: "group", title: t("New group") });
          }}
        >
          {t("New group")}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconExternalLink size={16} />}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openExternalLink();
          }}
        >
          {t("External link")}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

function ExternalLinkCreateModal() {
  const { t } = useTranslation();
  const request = useExternalLinkModal();
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (request) {
      setTitle("");
      setUrl("");
    }
  }, [request]);

  const submit = () => {
    const trimmed = url.trim();
    if (!trimmed || !request) return;
    request.onSubmit({ title: title.trim(), url: trimmed });
    closeExternalLinkModal();
  };

  return (
    <Modal
      opened={request !== null}
      onClose={closeExternalLinkModal}
      title={t("External link")}
      centered
    >
      <Stack>
        <TextInput
          label={t("Title")}
          placeholder={t("Link title")}
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
        />
        <TextInput
          label={t("URL")}
          placeholder="https://example.com"
          value={url}
          onChange={(e) => setUrl(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          required
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={closeExternalLinkModal}>
            {t("Cancel")}
          </Button>
          <Button onClick={submit} disabled={!url.trim()}>
            {t("Create")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface NodeMenuProps {
  node: NodeApi<SpaceTreeNode>;
  treeApi: TreeApi<SpaceTreeNode>;
  spaceId: string;
}

function NodeMenu({ node, treeApi, spaceId }: NodeMenuProps) {
  const { t } = useTranslation();
  const clipboard = useClipboard({ timeout: 500 });
  const { spaceSlug } = useParams();
  const { openDeleteModal } = useDeletePageModal();
  const [data, setData] = useAtom(treeDataAtom);
  const emit = useQueryEmit();
  const [exportOpened, { open: openExportModal, close: closeExportModal }] =
    useDisclosure(false);
  const [
    movePageModalOpened,
    { open: openMovePageModal, close: closeMoveSpaceModal },
  ] = useDisclosure(false);
  const [
    copyPageModalOpened,
    { open: openCopyPageModal, close: closeCopySpaceModal },
  ] = useDisclosure(false);
  const [
    tabMetaModalOpened,
    { open: openTabMetaModal, close: closeTabMetaModal },
  ] = useDisclosure(false);
  const [tabDescriptionDraft, setTabDescriptionDraft] = useState(
    node.data.tabMeta?.description ?? "",
  );
  const favoriteIds = useFavoriteIds("page", spaceId);
  const addFavorite = useAddFavoriteMutation();
  const removeFavorite = useRemoveFavoriteMutation();
  const isFavorited = favoriteIds.has(node.data.id);
  const updatePageMutation = useUpdatePageMutation();
  // @ts-ignore — jotai generic inference trips with strictNullChecks=false
  const [, setEditLink] = useAtom<EditLinkState | null, [EditLinkState | null], void>(editLinkAtom);

  const handleCopyLink = () => {
    const pageUrl =
      getAppUrl() + buildPageUrl(spaceSlug, node.data.slugId, node.data.name);
    clipboard.copy(pageUrl);
    notifications.show({ message: t("Link copied") });
  };

  const handleDuplicatePage = async () => {
    try {
      const duplicatedPage = await duplicatePage({
        pageId: node.id,
      });

      // Find the index of the current node
      const parentId =
        node.parent?.id === "__REACT_ARBORIST_INTERNAL_ROOT__"
          ? null
          : node.parent?.id;
      const siblings = parentId ? node.parent.children : treeApi?.props.data;
      const currentIndex =
        siblings?.findIndex((sibling) => sibling.id === node.id) || 0;
      const newIndex = currentIndex + 1;

      // Add the duplicated page to the tree
      const treeNodeData: SpaceTreeNode = {
        id: duplicatedPage.id,
        slugId: duplicatedPage.slugId,
        name: duplicatedPage.title,
        position: duplicatedPage.position,
        spaceId: duplicatedPage.spaceId,
        parentPageId: duplicatedPage.parentPageId,
        icon: duplicatedPage.icon,
        hasChildren: duplicatedPage.hasChildren,
        canEdit: true,
        children: [],
      };

      // Update local tree
      const simpleTree = new SimpleTree(data);
      simpleTree.create({
        parentId,
        index: newIndex,
        data: treeNodeData,
      });
      setData(simpleTree.data);

      // Emit socket event
      setTimeout(() => {
        emit({
          operation: "addTreeNode",
          spaceId: spaceId,
          payload: {
            parentId,
            index: newIndex,
            data: treeNodeData,
          },
        });
      }, 50);

      notifications.show({
        message: t("Page duplicated successfully"),
      });
    } catch (err) {
      notifications.show({
        message: err.response?.data.message || "An error occurred",
        color: "red",
      });
    }
  };

  return (
    <>
      <Menu shadow="md" width={200}>
        <Menu.Target>
          <ActionIcon
            variant="transparent"
            c="gray"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <IconDotsVertical
              style={{ width: rem(20), height: rem(20) }}
              stroke={2}
            />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconLink size={16} />}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCopyLink();
            }}
          >
            {t("Copy link")}
          </Menu.Item>

          <Menu.Item
            leftSection={isFavorited ? <IconStarFilled size={16} /> : <IconStar size={16} />}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isFavorited) {
                removeFavorite.mutate({ type: "page", pageId: node.data.id });
              } else {
                addFavorite.mutate({ type: "page", pageId: node.data.id });
              }
            }}
          >
            {isFavorited ? t("Remove from favorites") : t("Add to favorites")}
          </Menu.Item>

          {node.data.externalUrl &&
            treeApi.props.disableEdit !== true &&
            node.data.canEdit !== false && (
              <Menu.Item
                leftSection={<IconLink size={16} />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setEditLink({
                    pageId: node.data.id,
                    title: node.data.name || "",
                    url: node.data.externalUrl || "",
                  });
                }}
              >
                {t("Edit URL")}
              </Menu.Item>
            )}

          <Menu.Item
            leftSection={<IconFileExport size={16} />}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openExportModal();
            }}
          >
            {t("Export page")}
          </Menu.Item>

          {treeApi.props.disableEdit !== true &&
            node.data.canEdit !== false && (
              <Menu.Item
                leftSection={
                  node.data.hidden ? (
                    <IconEye size={16} />
                  ) : (
                    <IconEyeOff size={16} />
                  )
                }
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const nextHidden = !node.data.hidden;
                  // optimistic local update
                  const applyHidden = (nodes: SpaceTreeNode[]): SpaceTreeNode[] =>
                    nodes.map((n) =>
                      n.id === node.data.id
                        ? { ...n, hidden: nextHidden }
                        : n.children
                          ? { ...n, children: applyHidden(n.children) }
                          : n,
                    );
                  setData(applyHidden(data));
                  updatePageMutation.mutate({
                    pageId: node.data.id,
                    // @ts-ignore: hidden is supported by backend
                    hidden: nextHidden,
                  });
                }}
              >
                {node.data.hidden
                  ? t(
                      node.data.isGroup
                        ? "Show group"
                        : node.data.externalUrl
                          ? "Show link"
                          : "Show page",
                    )
                  : t(
                      node.data.isGroup
                        ? "Hide group"
                        : node.data.externalUrl
                          ? "Hide link"
                          : "Hide page",
                    )}
              </Menu.Item>
            )}

          {treeApi.props.disableEdit !== true &&
            node.data.canEdit !== false &&
            node.data.isGroup && (
              <Menu.Item
                leftSection={
                  node.data.groupCollapsed ? (
                    <IconFoldDown size={16} />
                  ) : (
                    <IconFold size={16} />
                  )
                }
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const nextCollapsed = !node.data.groupCollapsed;
                  const applyCollapsed = (
                    nodes: SpaceTreeNode[],
                  ): SpaceTreeNode[] =>
                    nodes.map((n) =>
                      n.id === node.data.id
                        ? { ...n, groupCollapsed: nextCollapsed }
                        : n.children
                          ? { ...n, children: applyCollapsed(n.children) }
                          : n,
                    );
                  setData(applyCollapsed(data));
                  updatePageMutation.mutate({
                    pageId: node.data.id,
                    groupCollapsed: nextCollapsed,
                  });
                }}
              >
                {node.data.groupCollapsed
                  ? t("Expand group by default")
                  : t("Collapse group by default")}
              </Menu.Item>
            )}

          {treeApi.props.disableEdit !== true &&
            node.data.canEdit !== false &&
            node.parent?.id === "__REACT_ARBORIST_INTERNAL_ROOT__" && (
              <>
                <Menu.Item
                  leftSection={
                    node.data.isTab ? (
                      <IconLayoutNavbarFilled size={16} />
                    ) : (
                      <IconLayoutNavbar size={16} />
                    )
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const nextIsTab = !node.data.isTab;
                    const applyTab = (
                      nodes: SpaceTreeNode[],
                    ): SpaceTreeNode[] =>
                      nodes.map((n) =>
                        n.id === node.data.id
                          ? { ...n, isTab: nextIsTab }
                          : n.children
                            ? { ...n, children: applyTab(n.children) }
                            : n,
                      );
                    setData(applyTab(data));
                    updatePageMutation.mutate({
                      pageId: node.data.id,
                      // @ts-ignore: isTab is supported by backend
                      isTab: nextIsTab,
                    });
                  }}
                >
                  {node.data.isTab
                    ? t("Unmark as version tab")
                    : t("Mark as version tab")}
                </Menu.Item>

                {node.data.isTab && (
                  <Menu.Item
                    leftSection={<IconFileDescription size={16} />}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openTabMetaModal();
                    }}
                  >
                    {t("Edit tab description")}
                  </Menu.Item>
                )}
              </>
            )}

          {treeApi.props.disableEdit !== true &&
            node.data.canEdit !== false && (
              <>
                <Menu.Item
                  leftSection={<IconCopy size={16} />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDuplicatePage();
                  }}
                >
                  {t("Duplicate")}
                </Menu.Item>

                <Menu.Item
                  leftSection={<IconArrowRight size={16} />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openMovePageModal();
                  }}
                >
                  {t("Move")}
                </Menu.Item>

                <MoveToTabMenuItems
                  page={node.data}
                  spaceId={spaceId}
                  onAfterMove={() => setData(deleteTreeNode(data, node.data.id))}
                />

                <Menu.Item
                  leftSection={<IconCopy size={16} />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openCopyPageModal();
                  }}
                >
                  {t("Copy to space")}
                </Menu.Item>

                <Menu.Divider />
                <Menu.Item
                  c="red"
                  leftSection={<IconTrash size={16} />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openDeleteModal({ onConfirm: () => treeApi?.delete(node) });
                  }}
                >
                  {t("Move to trash")}
                </Menu.Item>
              </>
            )}
        </Menu.Dropdown>
      </Menu>

      <MovePageModal
        pageId={node.id}
        slugId={node.data.slugId}
        currentSpaceSlug={spaceSlug}
        onClose={closeMoveSpaceModal}
        open={movePageModalOpened}
      />

      <CopyPageModal
        pageId={node.id}
        currentSpaceSlug={spaceSlug}
        onClose={closeCopySpaceModal}
        open={copyPageModalOpened}
      />

      <ExportModal
        type="page"
        id={node.id}
        open={exportOpened}
        onClose={closeExportModal}
      />

      <Modal
        opened={tabMetaModalOpened}
        onClose={closeTabMetaModal}
        title={t("Edit tab description")}
        centered
        size="md"
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {t(
              "Shown under the tab title in the public sidebar dropdown. Plain text, keep it short.",
            )}
          </Text>
          <TextInput
            value={tabDescriptionDraft}
            onChange={(e) => setTabDescriptionDraft(e.currentTarget.value)}
            placeholder={t("e.g. Current version, Legacy v1, …")}
            data-autofocus
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={closeTabMetaModal}>
              {t("Cancel")}
            </Button>
            <Button
              onClick={() => {
                const next = tabDescriptionDraft.trim();
                const nextMeta = next ? { description: next } : null;
                const applyMeta = (
                  nodes: SpaceTreeNode[],
                ): SpaceTreeNode[] =>
                  nodes.map((n) =>
                    n.id === node.data.id
                      ? { ...n, tabMeta: nextMeta }
                      : n.children
                        ? { ...n, children: applyMeta(n.children) }
                        : n,
                  );
                setData(applyMeta(data));
                updatePageMutation.mutate({
                  pageId: node.data.id,
                  // @ts-ignore: tabMeta is supported by backend
                  tabMeta: nextMeta,
                });
                closeTabMetaModal();
              }}
            >
              {t("Save")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

/**
 * Inline submenu rendered inside the page context menu. Lists tabs the
 * page can be moved into (excluding its current tab) and the special
 * "Loose" target. Click → backend `pages/move` with a fresh fractional-
 * indexing position; the moved subtree's `tab_id` cascades server-side.
 */
function MoveToTabMenuItems({
  page,
  spaceId,
  onAfterMove,
}: {
  page: SpaceTreeNode;
  spaceId: string;
  onAfterMove: () => void;
}) {
  const { t } = useTranslation();
  const { data: tabs = [] } = useTabsQuery(spaceId);

  // Only show this group when the space actually uses tabs.
  if (tabs.length === 0) return null;

  const currentTabId =
    typeof (page as any).tabId === "string" ? (page as any).tabId : null;

  const moveTo = async (targetTabId: string) => {
    try {
      // Compute a position at the end of the destination's top-level list.
      const res = await getAllSidebarPages({
        spaceId,
        tabId: targetTabId,
      });
      let lastPos: string | null = null;
      for (const p of res.pages ?? []) {
        for (const it of p.items as IPage[]) {
          if (!lastPos || (it.position ?? "") > lastPos) {
            lastPos = it.position ?? null;
          }
        }
      }
      const nextPosition = generateJitteredKeyBetweenClient(lastPos, null);
      await movePageService({
        pageId: page.id,
        parentPageId: null,
        position: nextPosition,
        // @ts-ignore: tabId supported by backend
        tabId: targetTabId,
      });
      notifications.show({ message: t("Page moved") });
      // Drop from current view; full sidebar refresh comes from invalidation.
      onAfterMove();
      queryClient.invalidateQueries({ queryKey: ["root-sidebar-pages"] });
      queryClient.invalidateQueries({ queryKey: ["sidebar-pages"] });
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.message ?? t("Failed to move page"),
        color: "red",
      });
    }
  };

  return (
    <>
      <Menu.Divider />
      <Menu.Label>{t("Move to tab")}</Menu.Label>
      {tabs
        .filter((tab) => tab.id !== currentTabId)
        .map((tab) => (
          <Menu.Item
            key={tab.id}
            leftSection={<IconArrowRight size={16} />}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              moveTo(tab.id);
            }}
          >
            {tab.name}
            {tab.isDefault ? (
              <Text component="span" size="xs" c="dimmed" ml={6}>
                ({t("default")})
              </Text>
            ) : null}
          </Menu.Item>
        ))}
    </>
  );
}

interface PageArrowProps {
  node: NodeApi<SpaceTreeNode>;
  onExpandTree?: () => void;
}

function PageArrow({ node, onExpandTree }: PageArrowProps) {
  useEffect(() => {
    if (node.isOpen) {
      onExpandTree();
    }
  }, []);

  return (
    <ActionIcon
      size={20}
      variant="subtle"
      c="gray"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        node.toggle();
        onExpandTree();
      }}
    >
      {node.isInternal ? (
        node.children && (node.children.length > 0 || node.data.hasChildren) ? (
          node.isOpen ? (
            <IconChevronDown stroke={2} size={18} />
          ) : (
            <IconChevronRight stroke={2} size={18} />
          )
        ) : (
          <IconPointFilled size={8} />
        )
      ) : null}
    </ActionIcon>
  );
}
